import React from "react";
import { useForm } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FinancialAccount, PaymentTransaction } from "@/types";
import { formatMoney } from "@/utils/format-money";
import { round } from "@/utils/round";
import { router } from "@inertiajs/react";
import { Badge } from "@/components/ui/badge";
import { CircleCheckIcon } from "lucide-react";

type Props = {
  payment_transaction: PaymentTransaction;
  financial_accounts: FinancialAccount[];
};

export function PaymentTransactionAllocationForm({
  payment_transaction,
  financial_accounts,
}: Props) {
  const base_amount = payment_transaction.allocation
    ? payment_transaction.allocation.base_amount.amount
    : "0.00";
  const vat_amount = payment_transaction.allocation
    ? payment_transaction.allocation.vat_amount.amount
    : "0.00";

  const financial_account_allocations = financial_accounts.map(
    (financial_account) => {
      const financial_account_allocation = (
        payment_transaction.allocation?.financial_account_allocations || []
      ).find(
        (allocation) =>
          allocation.financial_account.id === financial_account.id,
      );

      return financial_account_allocation
        ? {
            id: financial_account_allocation.id,
            financial_account_id:
              financial_account_allocation.financial_account.id,
            base_amount: financial_account_allocation.base_amount.amount,
            vat_amount: financial_account_allocation.vat_amount.amount,
            total_amount: financial_account_allocation.total_amount.amount,
          }
        : {
            financial_account_id: financial_account.id,
            base_amount: "0.00",
            vat_amount: "0.00",
            total_amount: "0.00",
          };
    },
  );

  const allocation = payment_transaction.allocation;

  const { data, setData, post, patch, processing, errors, transform, isDirty } =
    useForm({
      base_amount,
      vat_amount,
      financial_account_allocations,
    });

  transform((data) => {
    return { payment_transaction_allocation: data };
  });

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    if (payment_transaction.allocation) {
      patch(
        `/payment_transaction_allocations/${payment_transaction.allocation.id}`,
        {
          preserveScroll: "errors",
        },
      );
    } else {
      post(
        `/payment_transactions/${payment_transaction.id}/payment_transaction_allocations`,
        {
          preserveScroll: "errors",
        },
      );
    }
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold tracking-tight">Allocation</h2>
        {allocation && !allocation.verified && (
          <Button
            disabled={isDirty}
            type="button"
            onClick={() => {
              router.post(
                `/payment_transaction_allocations/${allocation.id}/verify`,
              );
            }}
          >
            Verify
          </Button>
        )}
        {allocation?.verified && (
          <Badge
            variant="secondary"
            className="bg-green-500/15 text-green-700 dark:bg-green-500/10 dark:text-green-400"
          >
            <CircleCheckIcon />
            Verified
          </Badge>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-4">
          <div></div>
          <div></div>
          <div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                const totalAmount = parseFloat(
                  payment_transaction.amount.amount,
                );

                setData("base_amount", round(totalAmount, 2).toString());
                setData("vat_amount", "0.00");

                const financialAccountAllocationData = {
                  base_amount: round(totalAmount / 2, 3).toString(),
                  vat_amount: "0.00",
                  total_amount: round(totalAmount / 2, 3).toString(),
                };

                setData(
                  "financial_account_allocations",
                  data.financial_account_allocations.map(
                    (financial_account_allocation) => {
                      return {
                        ...financial_account_allocation,
                        ...financialAccountAllocationData,
                      };
                    },
                  ),
                );
              }}
            >
              0%
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                const totalAmount = parseFloat(
                  payment_transaction.amount.amount,
                );
                const baseAmount = round(totalAmount / 1.2, 2);
                const vatAmount = round(totalAmount - baseAmount, 2);

                setData("base_amount", baseAmount.toString());
                setData("vat_amount", vatAmount.toString());

                const financialAccountAllocationData = {
                  base_amount: round(baseAmount / 2, 3).toString(),
                  vat_amount: round(vatAmount / 2, 3).toString(),
                  total_amount: round(totalAmount / 2, 3).toString(),
                };

                setData(
                  "financial_account_allocations",
                  data.financial_account_allocations.map(
                    (financial_account_allocation) => {
                      return {
                        ...financial_account_allocation,
                        ...financialAccountAllocationData,
                      };
                    },
                  ),
                );
              }}
            >
              20%
            </Button>
          </div>
          <div></div>

          <div></div>
          <Label>Base amount</Label>
          <Label>VAT amount</Label>
          <Label>Total amount</Label>

          <div className="text-right">Ruby Lift</div>
          <div>
            <Input
              type="number"
              value={data.base_amount}
              onChange={(e) => {
                setData("base_amount", e.target.value);
              }}
            />
            {errors.base_amount && (
              <p className="text-destructive text-sm">{errors.base_amount}</p>
            )}
          </div>
          <div>
            <Input
              type="number"
              value={data.vat_amount}
              onChange={(e) => {
                setData("vat_amount", e.target.value);
              }}
            />
            {errors.vat_amount && (
              <p className="text-destructive text-sm">{errors.vat_amount}</p>
            )}
          </div>
          <div className="pl-3 text-sm">
            {formatMoney(payment_transaction.amount)}
          </div>

          {data.financial_account_allocations.map(
            (financial_account_allocation) => {
              const financial_account = financial_accounts.find(
                (financial_account) => {
                  return (
                    financial_account.id ===
                    financial_account_allocation.financial_account_id
                  );
                },
              );
              if (!financial_account) {
                throw new Error("No financial account found");
              }

              return (
                <React.Fragment key={financial_account.id}>
                  <div className="text-right">
                    <input type="hidden" value={financial_account.id} />
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setData(
                          "financial_account_allocations",
                          data.financial_account_allocations.map(
                            (financial_account_allocation) => {
                              if (
                                financial_account_allocation.financial_account_id ===
                                financial_account.id
                              ) {
                                return {
                                  ...financial_account_allocation,
                                  base_amount: data.base_amount,
                                  vat_amount: data.vat_amount,
                                  total_amount:
                                    payment_transaction.amount.amount,
                                };
                              } else {
                                return {
                                  ...financial_account_allocation,
                                  base_amount: "0.00",
                                  vat_amount: "0.00",
                                  total_amount: "0.00",
                                };
                              }
                            },
                          ),
                        );
                      }}
                    >
                      💰
                    </Button>
                    {financial_account.name}
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={financial_account_allocation.base_amount}
                      onChange={(e) => {
                        const new_financial_account_allocations =
                          data.financial_account_allocations.map((faa) => {
                            if (
                              faa.financial_account_id ===
                              financial_account_allocation.financial_account_id
                            ) {
                              return {
                                ...faa,
                                base_amount: e.target.value,
                              };
                            } else {
                              return faa;
                            }
                          });

                        setData(
                          "financial_account_allocations",
                          new_financial_account_allocations,
                        );
                      }}
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={financial_account_allocation.vat_amount}
                      onChange={(e) => {
                        const new_financial_account_allocations =
                          data.financial_account_allocations.map((faa) => {
                            if (
                              faa.financial_account_id ===
                              financial_account_allocation.financial_account_id
                            ) {
                              return {
                                ...faa,
                                vat_amount: e.target.value,
                              };
                            } else {
                              return faa;
                            }
                          });

                        setData(
                          "financial_account_allocations",
                          new_financial_account_allocations,
                        );
                      }}
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={financial_account_allocation.total_amount}
                      onChange={(e) => {
                        const new_financial_account_allocations =
                          data.financial_account_allocations.map((faa) => {
                            if (
                              faa.financial_account_id ===
                              financial_account_allocation.financial_account_id
                            ) {
                              return {
                                ...faa,
                                total_amount: e.target.value,
                              };
                            } else {
                              return faa;
                            }
                          });

                        setData(
                          "financial_account_allocations",
                          new_financial_account_allocations,
                        );
                      }}
                    />
                  </div>
                </React.Fragment>
              );
            },
          )}
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={processing}>
            Allocate money
          </Button>
        </div>
      </form>
    </>
  );
}
